/**
 *
 * TopicItemSmall
 *
 */

import React from 'react';
import _classNames from 'classnames';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import _has from 'lodash/has';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { makeSelectAuth } from 'containers/Auth/selectors';
import { trackFavoriteAction } from 'containers/Favorites/utils';
import { makeSelectAllowSignIn } from 'containers/Main/selectors';
import { saveTopic } from 'containers/Topics/actions';
import { makeSelectSavedTopics } from 'containers/Topics/selectors';
import { isTopicSaved } from 'containers/Topics/utils';
import EvidenceTooltip from 'containers/TopicCenter/components/Overview/EvidenceTooltip';
import Badge from 'components/Badge';
import Image from 'components/ResourceItemSmall/components/Image';
import Title from 'components/ResourceItemSmall/components/Title';
import HeartEmptyIcon from 'components/SaveAsFavorite/images/heart-empty.svg';
import HeartFullIcon from 'components/SaveAsFavorite/images/heart-full.svg';
import Mixpanel from 'utils/mixpanelService';
import { isBot, isIE, fallbackImage } from 'utils/stringUtils';
import useAlgoliaLocale from 'components/useAlgoliaLocale';
import { getAlgoliaLocalizedField } from 'utils/localeUtils';

const useStyles = makeStyles(theme => ({
  wrapper: {
    position: 'relative',
  },
  openWrapper: {
    width: '200%',
    zIndex: 8,
    left: '-50%',
  },
  rightWrapper: {
    left: 0,
  },
  leftWrapper: {
    left: '-100%',
  },
  link: {
    textDecoration: 'none',
    position: 'relative',
  },
  container: {
    overflow: 'hidden',
    borderRadius: '5px !important',
    border: `1px solid #E9ECED`,
    height: 271,
    width: 'auto',
    position: 'relative',
    backgroundColor: '#FFFFFF',
  },
  imageVideoContainer: {
    height: 123,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  blurredBackground: {
    position: 'absolute',
    height: '100%',
    width: '100%',
    top: 0,
    left: 0,
    filter: 'blur(8px)',
    '-webkit-filter': 'blur(8px)',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
  },
  imageWrapper: {
    zIndex: 1,
  },
  contentWrapper: {
    padding: '2px 10px 8px 10px',
    zIndex: 1,
    backgroundColor: '#FFFFFF',
    height: 146,
  },
  infoWrapper: {
    maxHeight: 116,
  },
  badgeAndIconWrapper: {
    position: 'relative',
    marginBottom: 4,
  },
  iconButton: {
    position: 'absolute',
    top: 0,
    right: 0,
    height: '100%',
    display: 'flex',
    padding: 4,
    marginRight: -4,
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: 'inherit',
    },
  },
  icon: {
    width: 17,
    zIndex: 2,
  },
  desktopWrapper: {
    zIndex: 0,
  },
  descriptionWrapper: {
    ...theme.typography.pxSmall,
    display: '-webkit-box',
    margin: '0 auto',
    '-webkit-line-clamp': '5',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxHeight: 90 /* Fallback for non-webkit */,
  },
  description: {
    display: 'block',
  },
  quickTake: {
    display: 'block',
    marginTop: 8,
  },
  evidenceWrapper: {
    position: 'absolute',
    width: '100%',
    bottom: 4,
    display: 'flex',
    alignItems: 'center',
  },
  evidenceIcon: {
    height: 8,
    width: 8,
    borderRadius: 4,
    display: 'inline-block',
    marginRight: 6,
  },
  evidenceTitle: {
    fontFamily: 'PoppinsSemiBold',
    fontSize: '0.75rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '& div': {
      marginLeft: '0 !important',
    },
  },
}));

function TopicItemSmall({
  item,
  item: { title = '', slug, image = {}, imageUrl = '', minutes = '' },
  evidence,
  listTitle,
  isPdf,
  grayscale,
  trackingPath,
}) {
  const dispatch = useDispatch();
  const location = useLocation();
  const auth = useSelector(makeSelectAuth());
  const allowSignIn = useSelector(makeSelectAllowSignIn());
  const savedTopics = useSelector(makeSelectSavedTopics());
  const locale = useAlgoliaLocale();
  const classes = useStyles();

  const saved = isTopicSaved(savedTopics, item);

  const handleSave = e => {
    e.preventDefault();
    e.stopPropagation();

    const id = item.objectID || item.id;

    let nodeID;
    if (saved) [{ nodeID }] = savedTopics.filter(el => el.sys.id === id);

    const saveData = {
      nodeID,
      topic: {
        topicID: id,
        slug,
        name: title,
      },
      save: !saved,
    };
    trackFavoriteAction(
      'Resource Card',
      {
        resourceID: id,
        type: 'topic',
        slug,
        name: title,
        ...(trackingPath && { path: trackingPath }),
      },
      auth,
      saved,
    );
    dispatch(saveTopic(saveData));
  };

  const redirect = () => {
    const pageName = location.pathname.split('/')[1];
    const finalPageName = pageName ? `${pageName}Page` : 'homePage';
    Mixpanel.track('Resource Card - Clicked', {
      name: title,
      type: 'topic',
      page: finalPageName,
      path: trackingPath || location.pathname.split('/')[1],
      slug,
      listTitle,
      recommendation: undefined,
      subtype: undefined,
      blog: undefined,
      entryId: item.objectID || item.id,
    });
  };

  let file;
  if (!_isEmpty(imageUrl)) file = imageUrl;
  else if (_has(image, 'url')) file = image.url;
  else if (!_has(image, 'fields') && typeof image === 'object') file = {};
  else if (!_isEmpty(image) && typeof image === 'object')
    ({ file } = image.fields);
  else file = image;

  let imageSrc = _isEmpty(file)
    ? fallbackImage
    : _has(file, 'url')
    ? file.url
    : file;
  if (imageSrc.includes('.crediblemind.com')) {
    imageSrc = fallbackImage;
  }
  let finalImageSrcBackground;
  if (!isIE()) {
    if (imageSrc !== 'https://crediblemind.com/cm-logo-blue.jpg') {
      finalImageSrcBackground = imageSrc;
    }
  } else if (imageSrc.includes('ctfassets')) {
    finalImageSrcBackground = `${imageSrc}?h=16&w=16`;
  }

  const bot = isBot();

  return (
    <div className={classes.wrapper}>
      <Link to={`/topics/${slug}`} className={classes.link} onClick={redirect}>
        <Paper elevation={0} className={classes.container}>
          <div className={classes.desktopWrapper}>
            <Grid container>
              <Grid item xs={12} className={classes.imageVideoContainer}>
                {!_isNil(finalImageSrcBackground) && (
                  <div
                    className={classes.blurredBackground}
                    style={{
                      backgroundImage: `url(${finalImageSrcBackground})`,
                    }}
                  />
                )}
                <div className={classes.imageWrapper}>
                  <Image
                    file={file}
                    type="topic"
                    isPdf={isPdf}
                    grayscale={grayscale}
                  />
                </div>
              </Grid>
              <Grid item xs={12} className={classes.contentWrapper}>
                <Grid
                  container
                  className={_classNames({
                    [classes.infoWrapper]: evidence,
                  })}
                >
                  <Grid item xs={12} className={classes.badgeAndIconWrapper}>
                    <Badge
                      type="topic"
                      minutes={minutes}
                      removeMargin
                      data={item}
                    />
                    {!bot && allowSignIn && saved && (
                      <IconButton
                        className={classes.iconButton}
                        onClick={handleSave}
                        disableRipple
                        disableFocusRipple
                      >
                        <img
                          src={HeartFullIcon}
                          alt="Full"
                          className={classes.icon}
                        />
                      </IconButton>
                    )}
                    {!bot && allowSignIn && !saved && (
                      <IconButton
                        className={classes.iconButton}
                        onClick={handleSave}
                        disableRipple
                        disableFocusRipple
                      >
                        <img
                          src={HeartEmptyIcon}
                          alt="Empty"
                          className={classes.icon}
                        />
                      </IconButton>
                    )}
                  </Grid>
                  <Title
                    title={getAlgoliaLocalizedField(item, 'title', locale)}
                  />
                  <Grid item xs={12}>
                    <Typography
                      color="textPrimary"
                      align="left"
                      className={classes.descriptionWrapper}
                    >
                      <span className={classes.description}>
                        {getAlgoliaLocalizedField(item, 'description', locale)}
                      </span>
                    </Typography>
                  </Grid>
                </Grid>
                {evidence && (
                  <Box className={classes.evidenceWrapper}>
                    <Typography
                      className={classes.evidenceTitle}
                      color="textPrimary"
                    >
                      <EvidenceTooltip position="right">
                        <span
                          className={classes.evidenceIcon}
                          style={{
                            backgroundColor: item.evidenceColor,
                          }}
                        />
                      </EvidenceTooltip>
                      {item.evidenceName}
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          </div>
        </Paper>
      </Link>
    </div>
  );
}

TopicItemSmall.propTypes = {
  item: PropTypes.object,
  listTitle: PropTypes.string,
  evidence: PropTypes.bool,
  isPdf: PropTypes.bool,
};

export default React.memo(TopicItemSmall);
